import {useAtom} from "jotai";
import {useCallback, useEffect} from "react";
import {FlowProtectionSessionsAtom} from "../pages/project/atoms";
import {readFlowProtection, writeFlowProtection} from "../utils/flowProtectionStorage";

const DEFAULT_TTL_SECONDS = 900; // 15 minutes, matches backend session length

/**
 * Tracks the per-flow "protection unlock" session token used to view
 * protected user messages unmasked. Backed by an in-memory jotai atom
 * (for reactivity across mounted components) mirrored into localStorage as
 * `${flowUid}_token`, which ConversationRepository reads to attach the
 * X-Protection-Token header. The entry expires with the backend session.
 */
export const useFlowProtection = (flowUid) => {
  const [sessions, setSessions] = useAtom(FlowProtectionSessionsAtom);

  // Hydrate this flow's session from localStorage the first time it's used.
  useEffect(() => {
    if (!flowUid || sessions[flowUid]) return;
    const entry = readFlowProtection(flowUid);
    if (entry) {
      setSessions(prev => ({...prev, [flowUid]: entry}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowUid]);

  const session = flowUid ? sessions[flowUid] : null;
  const isUnlocked = !!(session?.token && session?.expiresAt > Date.now());

  const unlock = useCallback((token, expiresInSeconds = DEFAULT_TTL_SECONDS) => {
    if (!flowUid || !token) return;
    setSessions(prev => {
      const next = {...prev, [flowUid]: {token, expiresAt: Date.now() + (expiresInSeconds || DEFAULT_TTL_SECONDS) * 1000}};
      writeFlowProtection(flowUid, next[flowUid]);
      return next;
    });
  }, [flowUid, setSessions]);

  // Call after a request successfully used the token - the backend slides
  // the real session forward, this keeps the local expiry in sync.
  const extend = useCallback((expiresInSeconds = DEFAULT_TTL_SECONDS) => {
    if (!flowUid) return;
    setSessions(prev => {
      if (!prev[flowUid]?.token) return prev;
      const next = {...prev, [flowUid]: {...prev[flowUid], expiresAt: Date.now() + (expiresInSeconds || DEFAULT_TTL_SECONDS) * 1000}};
      writeFlowProtection(flowUid, next[flowUid]);
      return next;
    });
  }, [flowUid, setSessions]);

  const lock = useCallback(() => {
    if (!flowUid) return;
    setSessions(prev => {
      if (!(flowUid in prev)) return prev;
      const next = {...prev};
      delete next[flowUid];
      writeFlowProtection(flowUid, null);
      return next;
    });
  }, [flowUid, setSessions]);

  return {
    token: isUnlocked ? session.token : null,
    isUnlocked,
    expiresAt: session?.expiresAt || null,
    unlock,
    extend,
    lock,
  };
};
